"use client";

import { AnimatePresence, motion, MotionConfig } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { default as NextImage } from "next/image";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { swipePower, wrap } from "@trip/utils";

import { AngleLeftIcon } from "~/components/icons/angle-left";
import { AngleRightIcon } from "~/components/icons/angle-right";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/slideshow";
import { graphql } from "~/lib/dato-graphql";
import { getDatoLocale } from "~/lib/dato-utils";
import { responsiveImageFragment } from "~/lib/fragments";
import { useDatoQuery } from "~/lib/use-urql-queries";

import { type TourFromPage } from "../page";

type Direction = -1 | 1;

const swipeConfidenceThreshold = 10_000;

const TOUR_GALLERY_QUERY = graphql(
  `
    query TourGallery($id: ItemId!, $locale: SiteLocale!) {
      tour(locale: $locale, filter: { id: { eq: $id } }, fallbackLocales: en) {
        __typename
        gallery {
          id
          width
          height
          alt
          title
          responsiveImage(imgixParams: { auto: format, w: 1000, h: 700 }) {
            ...ResponsiveImageFragment
          }
        }
      }
    }
  `,
  [responsiveImageFragment],
);

// Use the Tour type from the parent page component
type InputTour = TourFromPage;

function Slideshow({ tour }: { tour: InputTour }) {
  const locale = useLocale();
  const tAccessibility = useTranslations("Accessibility");

  const [{ data }] = useDatoQuery({
    query: TOUR_GALLERY_QUERY,
    variables: {
      id: tour.id,
      locale: getDatoLocale(locale),
    },
  });

  const gallery = (data?.tour?.gallery ?? tour.gallery).flatMap(
    ({ responsiveImage, ...props }) =>
      responsiveImage
        ? [
            {
              ...responsiveImage,
              width:
                "width" in props && props.width
                  ? Math.min(props.width, responsiveImage.width)
                  : responsiveImage.width,
              height:
                "height" in props && props.height
                  ? Math.min(props.height, responsiveImage.height)
                  : responsiveImage.height,
            },
          ]
        : [],
  );

  const [[page, direction], setPage] = useState([0, 0]);

  const index = wrap(0, gallery.length, page);

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  const current = gallery[index];

  useHotkeys("ArrowRight", () => {
    paginate(1);
  });

  useHotkeys("ArrowLeft", () => {
    paginate(-1);
  });

  const collapsedAspectRatio = 1 / 2;
  const fullAspectRatio = 3 / 2;
  const margin = 2;
  const gap = 2;

  return (
    <MotionConfig transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}>
      <div className="flex h-full flex-col">
        <div className="relative flex h-full grow items-center gap-2">
          <Button
            onClick={(event) => {
              event.preventDefault();
              paginate(-1);
            }}
            size="circle"
            variant="ghost"
            className="absolute left-2 top-1/2 z-20 -translate-y-1/2 bg-white/20 sm:static sm:flex sm:bg-none"
          >
            <span className="sr-only">{tAccessibility("previousPage")}</span>
            <AngleLeftIcon />
          </Button>
          <AnimatePresence custom={direction} mode="popLayout">
            <motion.div
              animate="center"
              className="z-0 grow"
              custom={direction}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              exit="exit"
              initial="initial"
              key={`chunk-${index}`}
              onDragEnd={(_, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              transition={{
                opacity: { duration: 0.2 },
                x: { damping: 30, stiffness: 300, type: "spring" },
              }}
              variants={{
                center: {
                  opacity: 1,
                  x: 0,
                },
                exit: (direction: Direction) => ({
                  opacity: 0,
                  x: direction === 1 ? "-100%" : "100%",
                }),
                initial: (direction: Direction) => ({
                  opacity: 1,
                  x: direction === 1 ? "110%" : "-110%",
                }),
              }}
            >
              {current ? (
                <div key={current.src}>
                  <NextImage
                    alt=""
                    className="mx-auto max-h-[calc(100vh-200px)] w-auto"
                    draggable={false}
                    placeholder="blur"
                    blurDataURL={current.base64 ?? undefined}
                    src={current.src}
                    unoptimized={true}
                    width={current.width}
                    height={current.height}
                  />
                </div>
              ) : null}
            </motion.div>
          </AnimatePresence>
          <Button
            onClick={(event) => {
              event.preventDefault();
              paginate(1);
            }}
            size="circle"
            variant="ghost"
            className="absolute right-2 top-1/2 z-20 -translate-y-1/2 bg-white/20 sm:static sm:flex sm:bg-none"
          >
            <span className="sr-only">{tAccessibility("nextPage")}</span>
            <AngleRightIcon />
          </Button>
        </div>
        <div className="relative h-20 shrink-0">
          <div className="absolute inset-0 flex justify-center overflow-hidden">
            <motion.div
              initial={false}
              animate={{
                x: `-${
                  index * 100 * (collapsedAspectRatio / fullAspectRatio) +
                  margin +
                  index * gap
                }%`,
              }}
              style={{
                aspectRatio: fullAspectRatio,
                width: 120,
                gap: `${gap}%`,
              }}
              className="flex h-full"
            >
              {gallery.map((image, i) => (
                <motion.button
                  onClick={() => {
                    setPage([i, i > page ? 1 : -1]);
                  }}
                  initial={false}
                  animate={i === index ? "active" : "inactive"}
                  variants={{
                    active: {
                      aspectRatio: fullAspectRatio,
                      marginLeft: `${margin}%`,
                      marginRight: `${margin}%`,
                    },
                    inactive: {
                      aspectRatio: collapsedAspectRatio,
                      marginLeft: 0,
                      marginRight: 0,
                    },
                  }}
                  className="h-full shrink-0"
                  key={image.src}
                >
                  <img
                    src={image.src}
                    className="size-full max-w-none object-cover"
                    alt={image.alt ?? ""}
                  />
                </motion.button>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </MotionConfig>
  );
}

export function Gallery({ tour }: { tour: InputTour }) {
  const t = useTranslations("TourDetails");
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="frost">
          {t("showAllPhotos", { count: tour.gallery.length })}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("photosFrom", { title: tour.title })}</DialogTitle>
          <Slideshow tour={tour} />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
